import React from 'react';
import {useTranslation} from 'react-i18next';

import debounce from 'lodash/debounce';

import DeleteIcon from '@edna/icons/delete.svg';
import EditIcon from '@edna/icons/edit.svg';

import {Button} from '../Button';
import {Label} from '../FormField';
import {Spinner} from '../Spinner';
import {ErrorText, Icon, Stack} from '../primitives';
import {DEBOUNCE_DELAY, TContext, TImageInfo} from './definitions';
import * as S from './style';
import {formatImageSize, isValidUrl} from './utils';

const LoadingStatus = React.memo(() => {
  const {t} = useTranslation();

  return (
    <Stack direction="ROW" size="2" justify="center">
      <Spinner size="20px" color="gray" />
      <S.SecondaryText>{t('EdnaImagePicker:loading')}</S.SecondaryText>
    </Stack>
  );
});

const ImageInfo = React.memo<{imageInfo: TImageInfo; showName?: boolean; fileName?: string}>(
  ({imageInfo, showName, fileName}) => {
    const {t} = useTranslation();

    return (
      <Stack size="05">
        {showName && <S.ImageName>{fileName ?? imageInfo.name}</S.ImageName>}
        <S.SecondaryText>
          {t('EdnaImagePicker:imageInfo', {
            format: imageInfo.format.toUpperCase(),
            size: formatImageSize(imageInfo.size),
          })}
        </S.SecondaryText>
        {!!imageInfo.width && !!imageInfo.height && (
          <S.SecondaryText>
            {t('EdnaImagePicker:imageDimensions', {
              width: imageInfo.width,
              height: imageInfo.height,
            })}
          </S.SecondaryText>
        )}
      </Stack>
    );
  },
);

type TActionsProps = {
  context: TContext;
  error?: string;
  isInvalid?: boolean;
  readOnly?: boolean;
  onDelete?: TEmptyFunction;
};

type TUrlActionsProps = TActionsProps & {
  inputRef?: React.RefObject<HTMLInputElement | null>;
  onChange: (value: File[] | string) => void;
  updateContext: (values: TContext) => void;
  name?: string;
  onBlur?: TEmptyFunction;
};

const UrlActions = React.memo<TUrlActionsProps>(
  ({
    onChange,
    context,
    updateContext,
    error,
    name,
    inputRef,
    isInvalid,
    readOnly,
    onBlur,
    onDelete,
  }) => {
    const {t} = useTranslation();

    const debouncedHandler = React.useMemo(
      () => debounce((callback) => callback(), DEBOUNCE_DELAY),
      [],
    );

    const handleUrlChange = React.useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const url = event.target.value;

        if (!url) {
          updateContext({urlErrorKey: null});

          return;
        }

        if (!isValidUrl(url)) {
          updateContext({urlErrorKey: 'EdnaImagePicker:invalidUrl'});

          return;
        }

        debouncedHandler(() => onChange(url));
      },
      [onChange, updateContext],
    );

    const errorMessage = React.useMemo(() => {
      if (context.isLoading) {
        return null;
      }

      if (context.urlErrorKey) {
        return t(context.urlErrorKey);
      }

      return error;
    }, [t, context, error]);

    return (
      <>
        <S.GrowStack>
          <Label htmlFor={`${name}.imagePickerUrl`}>{t('EdnaImagePicker:urlLabel')}</Label>
          <S.TextInput
            innerRef={inputRef}
            name={`${name}.imagePickerUrl`}
            readOnly={readOnly || context.isLoading}
            onChange={handleUrlChange}
            onBlur={onBlur}
            isInvalid={!context.isLoading && (isInvalid || !!context.urlErrorKey)}
          />
          {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
          {context.isLoading && <LoadingStatus />}
        </S.GrowStack>
        {!!onDelete && (
          <Button
            size="S"
            square
            onClick={onDelete}
            disabled={context.isLoading}
            aria-label="delete"
          >
            <Icon as={DeleteIcon} size="12px" />
          </Button>
        )}
      </>
    );
  },
);

type TImageActionsProps = TActionsProps & {
  onEdit: TEmptyFunction;
  isImageOnly?: boolean;
  fileName?: string;
};

const ImageActions = React.memo<TImageActionsProps>(
  ({error, context, readOnly, isImageOnly, onDelete, onEdit, fileName}) => (
    <>
      <S.GrowStack>
        {isImageOnly && !context.isLoading && error && <ErrorText>{error}</ErrorText>}
        {context.isLoading && <LoadingStatus />}
        {!context.isLoading && !!context.imageInfo && (
          <S.GrowStack direction="ROW">
            <S.SuccessIcon readOnly={readOnly} />
            <ImageInfo imageInfo={context.imageInfo} showName fileName={fileName} />
          </S.GrowStack>
        )}
      </S.GrowStack>
      {!readOnly && (
        <Stack direction="ROW" size="2">
          {!!context.imageInfo && (
            <Button size="S" square onClick={onEdit} aria-label="edit" disabled={context.isLoading}>
              <Icon as={EditIcon} size="12px" />
            </Button>
          )}
          {!!onDelete && (
            <Button
              size="S"
              square
              onClick={onDelete}
              disabled={context.isLoading}
              aria-label="delete"
            >
              <Icon as={DeleteIcon} size="12px" />
            </Button>
          )}
        </Stack>
      )}
    </>
  ),
);

export {UrlActions, ImageActions};
